import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'

import { db } from 'utils/firebase'

import { Event } from 'types/event'
import { Data, Options } from 'types/query'

import { formatQuerySnapshot } from './formatFirestoreResults'
import { formatQueryOptions } from './formatQueryOptions'

export const fetchEventsByGroupIds = async (
  {
    groupIds,
    eventObject,
  }: {
    groupIds: string[] | undefined
    eventObject: 'all' | 'post'
  },
  options: Options<Data> = {
    orderDirection: 'desc',
  },
): Promise<Array<Event>> => {
  if (!groupIds) {
    return []
  }

  const groupEventDocs = groupIds.map((programId) => {
    let baseQuery = query(
      collection(db, 'events'),
      where('viewers.programs', 'array-contains', programId),
    )

    if (eventObject === 'post') {
      baseQuery = query(baseQuery, where('eventObject', '==', 'post'))
    }

    const queryWithOptions = formatQueryOptions(baseQuery, options)
    return getDocs(queryWithOptions)
  })

  const groupEventDocsResolve = await Promise.all(groupEventDocs)
  const eventsForGroupSnapshots: Array<Array<Event>> = groupEventDocsResolve.map((eventResolve) => {
    try {
      return formatQuerySnapshot(eventResolve)
    } catch (err) {
      return []
    }
  })
  let finalEvents: Array<Event> = []
  eventsForGroupSnapshots.forEach((snapshot) => {
    finalEvents = finalEvents.concat(snapshot)
  })

  return finalEvents
}

export const fetchPostEventsByUserId = async (
  {
    userId,
    eventObject,
  }: {
    userId: string | null | undefined
    eventObject: 'all' | 'post'
  },
  options: Options<Data> = {
    orderDirection: 'desc',
  },
): Promise<Array<Event>> => {
  let eventsForUserSnapshot: Array<Event> = []
  if (userId) {
    let userEventsQuery = query(
      collection(db, 'events'),
      where('viewers.users', 'array-contains', userId),
    )

    if (eventObject === 'post') {
      userEventsQuery = query(userEventsQuery, where('eventObject', '==', 'post'))
    }

    const queryWithOptions = formatQueryOptions(userEventsQuery, options)
    const userEventDocs = await getDocs(queryWithOptions)
    eventsForUserSnapshot = formatQuerySnapshot(userEventDocs)
  }

  return eventsForUserSnapshot
}

/**
 * Fetches filtered events to show as notifications
 */
export const fetchNotificationEventsByUserIdAndGroupIds = async ({
  userId,
  programIds,
}: {
  userId: string | null | undefined
  programIds: string[] | undefined
}): Promise<Array<Event>> => {
  if (!programIds) {
    return []
  }

  const groupEventDocs = programIds.map((programId) => {
    const baseQuery = query(
      collection(db, 'events'),
      where('viewers.programs', 'array-contains', programId),
      orderBy('created', 'desc'),
    )

    return getDocs(baseQuery)
  })

  let eventsForUserSnapshot: Array<Event> = []
  if (userId) {
    const userEventsQuery = query(
      collection(db, 'events'),
      where('viewers.users', 'array-contains', userId),
      orderBy('created', 'desc'),
    )

    const userEventDocs = await getDocs(userEventsQuery)
    eventsForUserSnapshot = formatQuerySnapshot(userEventDocs)
  }

  const groupEventDocsResolve = await Promise.all(groupEventDocs)

  const eventsForGroupSnapshots: Array<Array<Event>> = groupEventDocsResolve.map((eventResolve) => {
    try {
      return formatQuerySnapshot(eventResolve)
    } catch (err) {
      return []
    }
  })

  let finalEvents: Array<Event> = []
  eventsForGroupSnapshots.forEach((snapshot) => {
    finalEvents = finalEvents.concat(snapshot)
  })

  finalEvents = finalEvents.concat(eventsForUserSnapshot)
  finalEvents = finalEvents.filter((event) => {
    if (
      ['highFive', 'like', 'follow', 'accepted'].includes(event.eventType) &&
      (event.creator?.id !== userId || event.eventType === 'accepted')
    ) {
      return true
    }

    if (event.eventType === 'completion' && !event.post?.isArchived) {
      return true
    }

    return false
  })

  // It's possible that each query returned some of the same events.
  // We wouldn't want to display them twice.
  // This could also cause a crash because the underlying iOS AV Player cannot
  // load twice from the same source.
  const uniqueIds: string[] = []
  finalEvents = finalEvents.filter((event) => {
    const isDuplicate = uniqueIds.includes(event.id)

    if (!isDuplicate) {
      uniqueIds.push(event.id)

      return true
    }

    return false
  })

  finalEvents.sort((a, b) => {
    return b.created - a.created
  })

  return finalEvents
}

/**
 * Fetches events to display as user activity.
 * It is specifically filtered for the events that the UI is set up to display.
 * @param userId The user's ID to filter the events for.
 * @returns all post created and post completed events for a given user ID.
 */
export const fetchPostEventsCreatedByUserId = async (
  userId: string | null | undefined,
  options: Options<Data> = {
    orderDirection: 'desc',
  },
): Promise<Array<Event>> => {
  if (!userId || userId === '') return []

  const userEventsQuery = query(
    collection(db, 'events'),
    where('creator.id', '==', userId),
    where('eventObject', '==', 'post'),
  )

  const queryWithOptions = formatQueryOptions(userEventsQuery, options)
  const userEventDocs = await getDocs(queryWithOptions)
  return formatQuerySnapshot(userEventDocs) as Array<Event>
}

export const fetchPublicEvents = async (
  options: Options<Data> = {
    orderDirection: 'desc',
  },
): Promise<Array<Event>> => {
  // TODO: Figure out why multiple orderBys did not work.
  const baseQuery = query(
    collection(db, 'events'),
    // where('viewers.users', '!=', []),
    where('eventObject', '==', 'post'),
    // orderBy('viewers.users', 'asc'),
  )
  const queryWithOptions = formatQueryOptions(baseQuery, options)
  const querySnapshot = await getDocs(queryWithOptions)
  return formatQuerySnapshot(querySnapshot)
}
